import { useTranslation } from 'react-i18next';
import { SetStateAction, Dispatch } from 'react';

import { ContributionAmountResponseDto } from 'enrollment/queries/useContributionAmountQuery';
import { ContributionGraph, LumpSumInput } from './components';
import { formatDateString } from 'enrollment/features/EnrollmentDashboard/components/EnrollmentDashboardPage/utils';

interface ContributionLumpSumProps {
  contributionData: ContributionAmountResponseDto;
  planId: number;
  eeLumpSumAmount?: number;
  totalEeAnnual?: number;
  totalErAnnual?: number;
  displayEELumpSum?: boolean;
  displayERLumpSum?: boolean;
  setEeLumpSumAmount?: Dispatch<SetStateAction<number>>;
}

export const ContributionLumpSum = ({
  contributionData,
  planId,
  eeLumpSumAmount,
  totalEeAnnual,
  totalErAnnual,
  displayEELumpSum,
  displayERLumpSum,
  setEeLumpSumAmount,
}: ContributionLumpSumProps) => {
  const { t } = useTranslation('pages.financial');

  const eeContribution = Number(contributionData.data.subscriber_fsa_amount);
  const erContribution = contributionData.data.org_fsa_amount;
  const erLumpSumAmount =
    contributionData.data.er_lump_sum_contribution_amount || erContribution || 0;

  const irsMax =
    contributionData.data.irs_maximum ||
    contributionData.data.combined_max_amount ||
    0;

  const handleEeLumpSumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (setEeLumpSumAmount) {
      const maxEELumpSum = irsMax - ((totalErAnnual || 0) + (totalEeAnnual || 0));

      let lumpSumValue = Number(event.target.value);
      lumpSumValue = Math.min(
        Math.max(lumpSumValue, 0), maxEELumpSum
      );
      lumpSumValue = Math.round(lumpSumValue * 100) / 100;

      event.target.value = lumpSumValue.toFixed(2);
      setEeLumpSumAmount(lumpSumValue);
    }
  };

  return (
    <div>
      {displayEELumpSum && (
        <>
          <div className="row mt-4">
            <div className="col">
              <h3>{t('lumpSum.oneTime')}</h3>
              <p>{t('lumpSum.oneTimeDescription')}</p>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <LumpSumInput
                id={`ee-lump-sum-${planId}`}
                label={t('lumpSum.oneTime')}
                value={Number(Number(eeLumpSumAmount).toFixed(2))}
                helpText={t('lumpSum.eeLumpSumDetails', {
                  date: formatDateString(
                    contributionData?.data.hsa_contribution_end_dates[0]
                  ),
                })}
                onBlur={handleEeLumpSumChange}
              />
            </div>
          </div>
        </>
      )}
      {displayERLumpSum && (
        <>
          <div className="row mt-4">
            <div className="col">
              <h3>{t('employer')}</h3>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <LumpSumInput
                id="er-lump-sum"
                label={t('lumpSum.employerContribution')}
                value={erLumpSumAmount}
                disabled={true}
              />
            </div>
          </div>
        </>
      )}
      <div className="row mt-4">
        <div className="col">
          <h3>{t('lumpSum.contributionOverview')}</h3>
        </div>
      </div>
      <ContributionGraph
        eeContribution={eeContribution}
        eeLumpSumAmount={eeLumpSumAmount}
        erLumpSumAmount={erLumpSumAmount}
        irsMax={irsMax}
      />
    </div>
  );
};
